import { Suspense, lazy, FC } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';
import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';
import SuspenseLoader from 'src/components/SuspenseLoader';
import pages from './routes';
import AuthGuard from 'src/Guards/authGuard/AuthGuard';
import Unverified from 'src/pages/Fallbacks/Status/Unverified/Unverified';
import OpenTrades from 'src/pages/OpenTrades/OpenTrades';
import ReportsDashboard from 'src/pages/ReportsDashboard/ReportsDashboard';

const Loader = (Component: FC) => (props: any) =>
(
    <Suspense fallback={<SuspenseLoader />}>
        <Component {...props} />
    </Suspense>
);

// Pages
const Home = Loader(lazy(() => import('src/pages/Home/Home')));
const Register = Loader(lazy(() => import('src/pages/Register/Register')));
const Login = Loader(lazy(() => import('src/pages/Login/Login')));
const SystemDashboard = Loader(lazy(() => import('src/pages/SystemDashboard/SystemDashboard')));
const PlayersDashboard = Loader(lazy(() => import('src/pages/PlayersDashboard/PlayersDashboard')));
const PlayerDetailDashboard = Loader(lazy(() => import('src/pages/PlayerDetailDashboard/PlayerDetailDashboard')));
const Health = Loader(lazy(() => import('src/pages/Health/Health')));
const AlertsDashboard = Loader(lazy(() => import('src/pages/AlertsDashboard/AlertsDashboard')));
const RulesDashboard = Loader(lazy(() => import('src/pages/RulesDashboard/RulesDashboard')));
const RiskSettingsPage = Loader(lazy(() => import('src/pages/RiskSettings/RiskSettings')));

// Status
const Status404 = Loader(lazy(() => import('src/pages/Fallbacks/Status/Status404/Status404')));
const Status500 = Loader(lazy(() => import('src/pages/Fallbacks/Status/Status500/Status500')));
const StatusComingSoon = Loader(lazy(() => import('src/pages/Fallbacks/Status/ComingSoon/ComingSoon')));
const StatusMaintenance = Loader(lazy(() => import('src/pages/Fallbacks/Status/Maintenance/Maintenance')));
const StatusSuccess = Loader(lazy(() => import('src/pages/Fallbacks/Status/Success/Success')));
const StatusFailure = Loader(lazy(() => import('src/pages/Fallbacks/Status/Failure/Failure')));
const StatusCancel = Loader(lazy(() => import('src/pages/Fallbacks/Status/Cancel/Cancel')));

const routes: RouteObject[] = [
    {
        path: '',
        element: <BaseLayout />,
        /**
        * All children within this element will NOT have a Sidebar and top Navbar
        * All children within this element does not need to be authenticated to access
        */
        children: [
            //#region Base
            {
                //Navigate to home when base routed to base path
                path: '/',
                element: <Navigate to={pages.home.path} replace />,
            },
            //#endregion Base
            //#region Auth
            {
                //All authentication routes
                //No navbars are shown as the user is not logged in
                path: pages.auth.root,
                children: [
                    {
                        path: '',
                        element: <Login />,
                    },
                    {
                        path: pages.auth.login.name,
                        element: <Login />,
                    },
                    {
                        path: pages.auth.register.name,
                        element: <Register />,
                    },
                ]
            },
            //#endregion Auth
            //#region Status
            {
                //All status routes
                path: pages.status.root,
                children: [
                    {
                        path: '',
                        element: <Navigate to="404" replace />,
                    },
                    {
                        path: pages.status.unverified.name,
                        element: <Unverified />,
                    },
                    {
                        path: pages.status.status404.name,
                        element: <Status404 />,
                    },
                    {
                        path: pages.status.status500.name,
                        element: <Status500 />,
                    },
                    {
                        path: pages.status.statusMaintenance.name,
                        element: <StatusMaintenance />,
                    },
                    {
                        path: pages.status.statusComingSoon.name,
                        element: <StatusComingSoon />,
                    },
                    //TODO: Maybe make paths that shows status within the sidebars as well
                    {
                        path: pages.status.statusSuccess.name,
                        element: <StatusSuccess />,
                    },
                    {
                        path: pages.status.statusFailure.name,
                        element: <StatusFailure />,
                    },
                    {
                        path: pages.status.statusCancel.name,
                        element: <StatusCancel />,
                    },
                ],
            },
            //#endregion Status
            //#region health
            {
                //Navigate to home when base routed to base path
                path: '/health',
                element: <Health />,
            },
            //#endregion health
            //#region NotFound
            {
                path: '*',
                element: <Status404 />,
            },
            //#endregion NotFound
        ],
    },
    {
        path: '',
        element: (
            /**
             * All children with this element will have a Sidebar and top Navbar
             * AuthGuard checks that the user is logged in before granting access to its children pages
             */
            <AuthGuard>
                <SidebarLayout />
            </AuthGuard>
        ),
        children: [
            //#region Base
            {
                path: '',
                element: <Navigate to={pages.home.name} replace />,
            },
            //#endregion Base
            //#region Home
            {
                path: pages.home.name,
                element: <Home />,
            },
            //#endregion Home
            //#region System Health
            {
                path: pages.systemDashboard.name,
                element: <SystemDashboard />,
            },
            //#endregion System Health
            //#region players dashboard
            {
                path: pages.playersDashboard.name,
                element: <PlayersDashboard />,
            },
            //#endregion players dashboard
            //#region players detail dashboard
            {
                path: pages.playerDetailDashboard.path,
                element: <PlayerDetailDashboard />,
            },
            //#endregion players detail dashboard
            //#region alerts dashboard
            {
                path: pages.alertsDashboard.path,
                element: <AlertsDashboard />,
            },
            //#endregion alerts dashboard
            //#region rules dashboard
            {
                path: pages.rulesDashboard.path,
                element: <RulesDashboard />,
            },
            {
                path: pages.openTrades.name,
                element: <OpenTrades />,
            },
            //#endregion rules dashboard
            //#region reports dashboard
            {
                path: '/reports',
                element: <ReportsDashboard />,
            },
            //#endregion reports dashboard
            {
                path: pages.riskSettings.name,
                element: <RiskSettingsPage />,
            },
        ],
    },
];

export default routes;
